// ============================================
// COMPONENTE: Gráfico de Queixas com Seletor de Tipo
// ============================================

import { useState } from 'react';
import {
  BarChart, Bar,
  PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const CORES = ['#ffc107', '#e0a800', '#c69500', '#ad8200', '#946f00', '#7a5c00', '#614900', '#473600', '#2e2300', '#141000'];

type TipoGrafico = 'barra' | 'pizza' | 'radar';

interface Props {
  dados: Record<string, { 
    nome: string; 
    frequencia: number; 
    porcentagem: number;
  }>;
}

export default function GraficoQueixas({ dados }: Props) {
  const [tipoGrafico, setTipoGrafico] = useState<TipoGrafico>('barra');

  // Converter para array e ordenar
  const dadosArray = Object.entries(dados || {})
    .map(([id, d]) => ({
      id,
      nome: d.nome.length > 15 ? d.nome.substring(0, 15) + '...' : d.nome,
      nomeCompleto: d.nome,
      frequencia: d.frequencia,
      porcentagem: Math.round(d.porcentagem * 10) / 10
    }))
    .sort((a, b) => b.frequencia - a.frequencia)
    .slice(0, 10); // Top 10

  if (dadosArray.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-comments me-2"></i>
            Queixas Mais Frequentes
          </h3>
        </div>
        <div className="card-body">
          <p className="text-muted text-center py-4">
            <i className="fas fa-comments fa-3x mb-3 d-block text-secondary"></i>
            Nenhuma queixa registrada no período
          </p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border rounded p-2 shadow-sm">
          <p className="mb-1"><strong>{data.nomeCompleto}</strong></p>
          <p className="mb-0 text-warning">
            Frequência: {data.frequencia}x ({data.porcentagem}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderGrafico = () => {
    switch (tipoGrafico) {
      case 'pizza':
        return (
          <PieChart>
            <Pie
              data={dadosArray}
              cx="50%"
              cy="50%"
              outerRadius={120}
              dataKey="frequencia"
              nameKey="nome"
              label={({ nome, porcentagem }: { nome: string; porcentagem: number }) => 
                porcentagem > 5 ? `${nome}: ${porcentagem}%` : ''
              }
            >
              {dadosArray.map((_, index) => (
                <Cell key={`cell-${index}`} fill={CORES[index % CORES.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </PieChart>
        );

      case 'radar':
        return (
          <RadarChart cx="50%" cy="50%" outerRadius="80%" data={dadosArray}>
            <PolarGrid />
            <PolarAngleAxis dataKey="nome" tick={{ fontSize: 10 }} />
            <PolarRadiusAxis />
            <Radar 
              name="Frequência" 
              dataKey="frequencia" 
              stroke="#ffc107" 
              fill="#ffc107" 
              fillOpacity={0.5} 
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </RadarChart>
        );

      default: // barra
        return (
          <BarChart data={dadosArray} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="nome" 
              angle={-45}
              textAnchor="end"
              height={100}
              interval={0}
              tick={{ fontSize: 10 }}
            />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="frequencia" fill="#ffc107" name="Frequência" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
        <h3 className="card-title mb-0">
          <i className="fas fa-comments me-2"></i>
          Queixas Mais Frequentes
        </h3>
        <div className="btn-group btn-group-sm">
          <button
            className={`btn ${tipoGrafico === 'barra' ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setTipoGrafico('barra')}
            title="Barras"
          >
            <i className="fas fa-chart-bar"></i>
          </button>
          <button
            className={`btn ${tipoGrafico === 'pizza' ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setTipoGrafico('pizza')}
            title="Pizza"
          >
            <i className="fas fa-chart-pie"></i>
          </button>
          <button
            className={`btn ${tipoGrafico === 'radar' ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setTipoGrafico('radar')}
            title="Radar"
          >
            <i className="fas fa-spider"></i>
          </button>
        </div>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={350}>
          {renderGrafico()}
        </ResponsiveContainer>
        <div className="text-center mt-2">
          <small className="text-muted">
            Top 10 queixas mais frequentes
          </small>
        </div>
      </div>
    </div>
  );
}
